import React, { useEffect, useState } from 'react';
import {
  Bell, Building2, MapPin, Clock, ChevronDown, ChevronUp, Filter,
  Loader2, Briefcase, Linkedin, ExternalLink, UserSearch, Zap, Eye,
} from 'lucide-react';
import { IntelligenceLayout, fetchWithAuth, MetricCard, useCompanyFilters, CompanyFilterBar, CompanyTypeBadge } from './IntelligenceLayout';
import { WatchlistButton } from '../components/WatchlistButton';
import { TierBadge, ConfidenceBadge, TierFilterBar } from '../components/TierBadges';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FRESHNESS_TIERS = [
  { id: '24h', label: 'New (24h)', color: 'bg-red-600 text-white' },
  { id: '48h', label: 'Recent (48h)', color: 'bg-orange-500 text-white' },
  { id: '7d', label: 'Active (7d)', color: 'bg-blue-500 text-white' },
];

const FreshnessBadge = ({ freshness }) => {
  let color = 'bg-blue-50 text-blue-700 border-blue-200';
  if (freshness === 'New (24h)') color = 'bg-red-50 text-red-700 border-red-200';
  else if (freshness === 'Recent (48h)') color = 'bg-orange-50 text-orange-700 border-orange-200';
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold border ${color}`} data-testid="freshness-badge">
      <Clock className="h-3 w-3" />{freshness}
    </span>
  );
};

const DecisionMakerPanel = ({ company, makers, expanded, onToggle }) => {
  if (!expanded) return null;
  return (
    <tr className="bg-blue-50/30">
      <td colSpan={8} className="px-6 py-4">
        <div className="mb-2">
          <h4 className="text-xs font-semibold text-[#0A1F44] uppercase tracking-wider">Decision Makers — {company}</h4>
          <p className="text-[10px] text-gray-500">Click to search LinkedIn for hiring authorities at this company</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {makers.map((dm, i) => (
            <a key={i} href={dm.linkedin_search_url} target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-between p-2.5 bg-white border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50/30 transition-all group"
              data-testid={`scanner-dm-${i}`}>
              <div className="flex items-center gap-2">
                <div className="h-7 w-7 bg-blue-50 rounded-lg flex items-center justify-center">
                  <UserSearch className="h-3.5 w-3.5 text-blue-600" />
                </div>
                <div>
                  <div className="text-xs font-medium text-[#0A1F44]">{dm.title}</div>
                </div>
              </div>
              <div className="flex items-center gap-1 text-[10px] text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
                <Linkedin className="h-3 w-3" /><ExternalLink className="h-2.5 w-2.5" />
              </div>
            </a>
          ))}
        </div>
      </td>
    </tr>
  );
};

const HiringScanner = ({ embedded = false }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [freshness, setFreshness] = useState('7d');
  const [sortField, setSortField] = useState('new_postings');
  const [sortDir, setSortDir] = useState('desc');
  const [expandedRow, setExpandedRow] = useState(null);
  const [tierFilter, setTierFilter] = useState('');
  const [showStrategic, setShowStrategic] = useState(false);
  const { excludeStaffing, setExcludeStaffing, excludeRetail, setExcludeRetail, filterParams } = useCompanyFilters();

  useEffect(() => {
    setLoading(true);
    setExpandedRow(null);
    fetchWithAuth(`${API_URL}/api/intelligence/hiring-scanner?freshness=${freshness}${filterParams}`)
      .then(d => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [freshness, filterParams]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(field); setSortDir('desc'); }
  };

  let filtered = [...data];
  if (!showStrategic) filtered = filtered.filter(d => d.priority_tier !== 'strategic_enterprise');
  if (tierFilter) filtered = filtered.filter(d => d.priority_tier === tierFilter);

  const sorted = [...filtered].sort((a, b) => {
    const mul = sortDir === 'desc' ? -1 : 1;
    if (sortField === 'company') return mul * a.company.localeCompare(b.company);
    return mul * ((a[sortField] || 0) - (b[sortField] || 0));
  });

  const tierSummary = {};
  data.forEach(d => { tierSummary[d.priority_tier] = (tierSummary[d.priority_tier] || 0) + 1; });
  const newCount = data.filter(d => d.posting_freshness === 'New (24h)').length;
  const recentCount = data.filter(d => d.posting_freshness === 'Recent (48h)').length;
  const totalNewPostings = data.reduce((s, d) => s + d.new_postings, 0);

  const SortHeader = ({ field, label, align = 'left' }) => (
    <th className={`text-${align} px-4 py-3 cursor-pointer hover:text-[#0A1F44] select-none whitespace-nowrap`} onClick={() => handleSort(field)}>
      <span className="inline-flex items-center gap-1">{label}
        {sortField === field && (sortDir === 'desc' ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />)}
      </span>
    </th>
  );

  return (
    <IntelligenceLayout title="Hiring Scanner" embedded={embedded}>
      <header className="bg-white border-b border-gray-200 px-8 py-5 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 bg-red-50 rounded-xl flex items-center justify-center">
            <Bell className="h-5 w-5 text-red-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#0A1F44]" data-testid="scanner-title">New Hiring Signals</h1>
            <p className="text-sm text-gray-500">Companies posting new jobs — get in front of hiring managers before other agencies</p>
          </div>
        </div>
      </header>

      <main className="p-8 space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-20 gap-2 text-gray-400"><Loader2 className="h-5 w-5 animate-spin" /> Scanning hiring signals...</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="scanner-metrics">
              <MetricCard label="Companies Detected" value={data.length} icon={Building2} />
              <MetricCard label="Best Immediate" value={tierSummary.best_immediate || 0} icon={Zap} />
              <MetricCard label="Needs Enrichment" value={tierSummary.needs_enrichment || 0} icon={Bell} />
              <MetricCard label="New (24h)" value={newCount} icon={Clock} />
              <MetricCard label="Total New Postings" value={totalNewPostings} icon={Briefcase} />
            </div>

            <div className="flex items-center gap-4 flex-wrap bg-white border border-gray-200 rounded-xl px-4 py-3" data-testid="tier-filter-section">
              <TierFilterBar activeTier={tierFilter} onTierChange={setTierFilter} tierSummary={tierSummary} showStrategic={showStrategic} />
              <div className="ml-auto">
                <button onClick={() => setShowStrategic(!showStrategic)}
                  className={`flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg transition-all border ${showStrategic ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}
                  data-testid="toggle-strategic-enterprise">
                  <Eye className="h-3 w-3" /> Strategic Enterprise
                </button>
              </div>
            </div>

            <CompanyFilterBar excludeStaffing={excludeStaffing} setExcludeStaffing={setExcludeStaffing} excludeRetail={excludeRetail} setExcludeRetail={setExcludeRetail} />

            <div className="flex items-center gap-4" data-testid="freshness-filters">
              <Filter className="h-4 w-4 text-gray-400" />
              <span className="text-sm text-gray-600 font-medium">Time Window:</span>
              {FRESHNESS_TIERS.map(t => (
                <button key={t.id} onClick={() => setFreshness(t.id)} data-testid={`freshness-${t.id}`}
                  className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all ${freshness === t.id ? t.color + ' shadow-md' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'}`}>
                  {t.label}
                </button>
              ))}
              <span className="text-xs text-gray-400 ml-2">({data.length} companies)</span>
            </div>

            {data.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
                <Bell className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-gray-600 mb-1">No new hiring signals in this window</h3>
                <p className="text-sm text-gray-400">Try expanding the time window to 48h or 7d, or trigger a data ingestion to pull fresh postings.</p>
              </div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm" data-testid="scanner-table">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-gray-500 uppercase border-b border-gray-100 bg-gray-50">
                        <SortHeader field="company" label="Company" />
                        <th className="text-center px-4 py-3">Tier</th>
                        <th className="text-center px-4 py-3">Freshness</th>
                        <SortHeader field="new_postings" label="New Posts" align="right" />
                        <th className="text-left px-4 py-3">Top Role</th>
                        <th className="text-left px-4 py-3">Industry</th>
                        <th className="text-left px-4 py-3">Locations</th>
                        <SortHeader field="hiring_velocity" label="Total Roles" align="right" />
                        <th className="text-center px-4 py-3">Decision Makers</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.map((item, i) => (
                        <React.Fragment key={item.company}>
                          <tr className={`border-t border-gray-100 transition-colors ${item.posting_freshness === 'New (24h)' ? 'bg-red-50/20' : 'hover:bg-[#f8f6f0]'}`} data-testid={`scanner-row-${i}`}>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <WatchlistButton company={item.company} sourceModule="hiring-scanner" />
                                <div>
                                  <div className="font-medium text-[#0A1F44]">{item.company}</div>
                                  <ConfidenceBadge confidence={item.firmographic_confidence} flags={item.confidence_flags} />
                                </div>
                              </div>
                              {item.unique_roles && item.unique_roles.length > 1 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {item.unique_roles.slice(1, 3).map((r, j) => (
                                    <span key={j} className="text-[9px] text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded border border-gray-100">{r}</span>
                                  ))}
                                  {item.unique_roles.length > 3 && <span className="text-[9px] text-gray-400">+{item.unique_roles.length - 3} more</span>}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center"><TierBadge tier={item.priority_tier} /></td>
                            <td className="px-4 py-3 text-center"><FreshnessBadge freshness={item.posting_freshness} /></td>
                            <td className="px-4 py-3 text-right">
                              <span className="font-bold text-[#0A1F44]">{item.new_postings}</span>
                              {item.count_24h > 0 && <span className="block text-[9px] text-red-500">{item.count_24h} in 24h</span>}
                            </td>
                            <td className="px-4 py-3 text-gray-600 max-w-[180px] truncate">{item.top_title || '—'}</td>
                            <td className="px-4 py-3">
                              {item.industry ? <span className="px-2 py-0.5 rounded text-[10px] bg-gray-100 text-gray-600 border border-gray-200">{item.industry}</span> : '—'}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1">
                                {(item.locations || []).slice(0, 2).map((loc, j) => (
                                  <span key={j} className="inline-flex items-center gap-0.5 text-xs text-gray-500"><MapPin className="h-3 w-3 text-gray-400" />{loc}</span>
                                ))}
                                {(item.locations || []).length > 2 && <span className="text-[9px] text-gray-400">+{item.locations.length - 2}</span>}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-[#0A1F44]">{item.hiring_velocity}</td>
                            <td className="px-4 py-3 text-center">
                              <button onClick={() => setExpandedRow(expandedRow === i ? null : i)}
                                className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg transition-colors ${expandedRow === i ? 'bg-blue-600 text-white' : 'text-blue-600 bg-blue-50 border border-blue-200 hover:bg-blue-100'}`}
                                data-testid={`find-dm-${i}`}>
                                <UserSearch className="h-3 w-3" /> {expandedRow === i ? 'Hide' : 'Find'}
                              </button>
                            </td>
                          </tr>
                          <DecisionMakerPanel
                            company={item.company}
                            makers={item.decision_makers || []}
                            expanded={expandedRow === i}
                            onToggle={() => setExpandedRow(expandedRow === i ? null : i)}
                          />
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm" data-testid="scanner-legend">
              <h3 className="text-sm font-semibold text-[#0A1F44] mb-3 uppercase tracking-wider">Posting Freshness Tiers</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
                  <Zap className="h-5 w-5 text-red-600" />
                  <div>
                    <div className="text-sm font-semibold text-red-700">New (24h)</div>
                    <div className="text-[10px] text-red-600">Posted in the last 24 hours — highest urgency</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                  <Clock className="h-5 w-5 text-orange-600" />
                  <div>
                    <div className="text-sm font-semibold text-orange-700">Recent (48h)</div>
                    <div className="text-[10px] text-orange-600">Posted in the last 48 hours — still fresh opportunity</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <Briefcase className="h-5 w-5 text-blue-600" />
                  <div>
                    <div className="text-sm font-semibold text-blue-700">Active (7d)</div>
                    <div className="text-[10px] text-blue-600">Posted in the last 7 days — active hiring signal</div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </main>
    </IntelligenceLayout>
  );
};

export default HiringScanner;
